/**
 * Requirement: `#6620` - the OTR calculator's job execution type.
 *
 * Values are produced by the calculator (`functions/calculators/ymm/ymm-otr-calculator-*.ts`,
 * `JOB_EXECUTION_TYPES`) and reach the frontend two ways: on `metadata_calculatorOutput__` for a
 * job, and as the `calculator_jobExecutionType` report column. The calculator keeps its own copy of
 * these strings because it runs inside QuickJS and cannot import frontend code — so if these ever
 * change, both sides must change together.
 *
 * Consumers branch on the RAW value, never on the label. The engine also exposes a mapped
 * `jobExecutionType` column whose cells carry the display text, but that label is defined in
 * `backend/` and would break these comparisons silently the moment someone reworded it.
 */
export const JOB_EXECUTION_PARTNER_FP = 'partnerFp';
export const JOB_EXECUTION_OTR_OPERATED = 'otrOperated';
export const JOB_EXECUTION_THIRD_PARTY = 'thirdParty';

/** Display text, matching the calculator's own dropdown options. */
export const JOB_EXECUTION_LABELS: Record<string, string> = {
  [JOB_EXECUTION_PARTNER_FP]: 'Partner FP',
  [JOB_EXECUTION_OTR_OPERATED]: 'OTR-Operated',
  [JOB_EXECUTION_THIRD_PARTY]: 'Third-Party',
};

/**
 * Normalises a stored value to a known execution type.
 *
 * Anything unrecognised means "no execution type recorded" and resolves to Partner FP, matching the
 * `ELSE` branch of the engine's mapped column so the two can never disagree about the same job.
 * That covers a job last calculated before `#6620` (no value at all) and the report engine's null
 * cell, which serialises as the literal string `'None'` — truthy, so a plain `|| fallback` would
 * let it through as an execution type called "None".
 *
 * Membership is tested with `hasOwnProperty` rather than truthiness, or the inherited keys of
 * `Object.prototype` would pass: `JOB_EXECUTION_LABELS['constructor']` is a function, so
 * `'constructor'`, `'toString'` and friends would be returned as execution types and the fallback
 * documented above would not hold for them.
 */
export function normalizeJobExecutionType(raw: unknown): string {
  return typeof raw === 'string' && Object.prototype.hasOwnProperty.call(JOB_EXECUTION_LABELS, raw)
    ? raw
    : JOB_EXECUTION_PARTNER_FP;
}

/** True when the job type leaves no Franchise Partner to pay, so the FP payout is zeroed. */
export function jobExecutionZeroesFpPayout(type: string): boolean {
  return type === JOB_EXECUTION_OTR_OPERATED || type === JOB_EXECUTION_THIRD_PARTY;
}
